import { SegmentedControl } from "@/frontend/components/ui/segmented-control";
import {
  ENVIRONMENT_TOGGLE_OPTIONS,
  ORG_ENVIRONMENT_LABEL,
  type OrgEnvironment,
  type WorkspaceEnvironmentOption,
} from "@/frontend/types/org";
import { cn } from "@/frontend/lib/utils";

type Props = {
  /** Both halves of the pair. Segments missing a sibling are not rendered. */
  siblings: ReadonlyArray<WorkspaceEnvironmentOption>;
  /** Environment of the workspace currently active in the session. */
  activeEnvironment: OrgEnvironment;
  /** Called with the sibling's organization id when the user picks the other environment. */
  onSelect: (organizationId: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Sandbox | Production toggle for a paired workspace. Renders nothing unless
 * both halves are present, so an unpaired workspace never shows a dead segment.
 */
export function EnvironmentToggle({
  siblings,
  activeEnvironment,
  onSelect,
  disabled = false,
  className,
}: Props) {
  const options = ENVIRONMENT_TOGGLE_OPTIONS.filter((option) =>
    siblings.some((sibling) => sibling.environment === option.value),
  );
  if (options.length < 2) return null;

  /** Switches to the sibling serving `environment`; a no-op when it is already active. */
  const handleChange = (environment: OrgEnvironment) => {
    if (environment === activeEnvironment) return;
    const target = siblings.find((sibling) => sibling.environment === environment);
    if (target) onSelect(target.id);
  };

  return (
    <SegmentedControl
      size="md"
      variant="emphasis"
      aria-label={`Workspace environment, currently ${ORG_ENVIRONMENT_LABEL[activeEnvironment]}`}
      value={activeEnvironment}
      options={options}
      onChange={handleChange}
      disabled={disabled}
      className={cn("h-8 w-full [&>button]:flex-1", className)}
    />
  );
}
